import { type AppLocale } from '@irl/types';

import { normalizeTimezone } from '@/lib/timezone-options';

import { en } from './locales/en.js';
import { ru, type TranslationTree } from './locales/ru.js';

const dictionaries: Record<AppLocale, TranslationTree> = { ru, en };

function resolvePath(tree: TranslationTree, key: string): string | undefined {
  const parts = key.split('.');
  let current: unknown = tree;

  for (const part of parts) {
    if (!current || typeof current !== 'object' || !(part in current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return typeof current === 'string' ? current : undefined;
}

export function createTranslator(locale: AppLocale) {
  const dictionary = dictionaries[locale];

  return function t(key: string, vars?: Record<string, string | number>): string {
    const fallback = resolvePath(dictionaries.ru, key) ?? key;
    let text = resolvePath(dictionary, key) ?? fallback;

    if (vars) {
      for (const [name, value] of Object.entries(vars)) {
        text = text.replaceAll(`{{${name}}}`, String(value));
      }
    }

    return text;
  };
}

function toDate(value: string | Date): Date {
  return typeof value === 'string' ? new Date(value) : value;
}

function localeTag(locale: AppLocale): string {
  return locale === 'en' ? 'en-US' : 'ru-RU';
}

export function formatDateTime(value: string | Date, locale: AppLocale): string {
  return toDate(value).toLocaleString(localeTag(locale));
}

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((part) => part.type === type)?.value ?? '';
}

/** Absolute instant in reminder IANA timezone — matches scheduleConfig.time (HH:mm). */
export function formatDateTimeInTimezone(
  value: string | Date,
  timezone: string,
  locale: AppLocale,
): string {
  const date = toDate(value);
  const timeZone = normalizeTimezone(timezone);

  try {
    const parts = new Intl.DateTimeFormat(localeTag(locale), {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const day = partValue(parts, 'day');
    const month = partValue(parts, 'month');
    const year = partValue(parts, 'year');
    const hour = partValue(parts, 'hour');
    const minute = partValue(parts, 'minute');

    return `${day}.${month}.${year}, ${hour}:${minute}`;
  } catch {
    return formatDateTime(date, locale);
  }
}
